import {ReactNode} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {Link, Typography} from '@mui/material';
import {Box} from '@jobseeker/lotus/foundations';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {VersionVariants} from '@jobseeker/lotus/enums';
import Breadcrumbs from '@jobseeker/lotus/components/Breadcrumbs';
import {BreadcrumbJsonLd} from 'next-seo';
import ErrorBoundary from '@components/ErrorBoundary';
import {truncateString} from '@utils';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  COURSE,
  EPOCH,
  PENDING_RECOMMENDATIONS,
  PERSONALIZE_JOBFEED,
  SOCIAL_CONNECTION,
} from '@constants';
import {HomeBread} from '@jobseeker/lotus/assets/icons';

import type {AppState} from '@jobseeker/common-core/types';

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

interface LayoutProps {
  children: ReactNode;
  breadcrumbElements: {name: string; item: string}[];
  variant?: string;
}

export const getBreadcrumbsElements = (
  breadcrumbElements: Array<{
    name: string;
    item: string;
  }>,
  breadCrumbWithIcon?: boolean,
  device = 'desktop'
) => {
  return breadcrumbElements?.map((element, index) => {
    if (element?.item && index < element.item.length) {
      return (
        <Typography
          key={`breadcrumb-layout-${index}`}
          fontSize={breadCrumbWithIcon ? 12 : 10}
          color={breadCrumbWithIcon ? '#898989' : 'secondary.main'}
          fontWeight={500}
        >
          {element?.name === 'Home' && breadCrumbWithIcon && (
            <Image
              src={HomeBread}
              alt={'home-bread'}
              width={10}
              height={10}
              style={{
                marginRight: '8px',
              }}
            />
          )}

          <Link
            href={element?.item}
            sx={{
              textDecoration: 'none',
              color: breadCrumbWithIcon ? '#898989' : 'secondary.main',
              ':hover': {
                textDecoration: breadCrumbWithIcon ? 'underline' : 'none',
              },
            }}
          >
            {device === 'desktop'
              ? element?.name
              : truncateString(element?.name, 10)}
          </Link>
        </Typography>
      );
    }
    return (
      <Typography
        key={`breadcrumb-title-${index}`}
        fontSize={breadCrumbWithIcon ? 12 : 10}
        color={'secondary.main'}
        sx={{
          overflowX: 'hidden',
          overflowY: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        textTransform={'capitalize'}
      >
        {device === 'mobile'
          ? truncateString(element?.name, isAppIimjobs ? 24 : 15)
          : element?.name}
      </Typography>
    );
  });
};

export const getAppendedLinkForBreadcrumb = (
  breadcrumbElements: Array<{
    name: string;
    item: string;
  }>
) => {
  return breadcrumbElements?.map((breadCrumbInfo, index) => {
    if (breadCrumbInfo?.name) {
      const breadElement: {position: number; name: string; item?: string} = {
        position: index + 1,
        name: breadCrumbInfo?.name,
      };
      if (breadcrumbElements?.length - 1 !== index) {
        breadElement['item'] = `${
          process.env.NEXT_PUBLIC_LOAD_CONFIG
        }${breadCrumbInfo?.item?.split('?')[0]}`;
      }
      return breadElement;
    }
    return breadCrumbInfo;
  });
};

const ResponsiveLayout = (props: LayoutProps) => {
  const {children, breadcrumbElements, variant} = props || {};

  const isAboveXl = useIsLargeDevice(2560);
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const breadCrumbs = getBreadcrumbsElements(
    breadcrumbElements,
    !isAppIimjobs,
    device
  );

  const boxSx = {
    background:
      variant === VersionVariants.VERSION_TWO
        ? isAppIimjobs
          ? '#F3F7FA'
          : '#F2F5FA'
        : '#fff',
    py: {
      xxs: breadcrumbElements.length
        ? isAppIimjobs
          ? 10
          : 6.9375
        : page?.indexOf(EPOCH) > -1
        ? 6
        : isAppIimjobs
        ? 8
        : 7,
      md:
        page == 'jobfeed'
          ? isAppIimjobs
            ? 10
            : 8
          : page?.indexOf(EPOCH) > -1
          ? 8
          : isAppIimjobs
          ? 10
          : 8,
    },
    minHeight:
      page?.indexOf(PERSONALIZE_JOBFEED) > -1 ||
      page?.includes('registration-') ||
      page?.indexOf(SOCIAL_CONNECTION) > -1 ||
      page?.indexOf(PENDING_RECOMMENDATIONS) > -1
        ? '100vh'
        : '58vh',
    ...(isAppIimjobs
      ? {
          px:
            variant === VersionVariants.VERSION_TWO
              ? 2
              : !isAboveXl
              ? {xxs: 2, md: 4, xl: 15}
              : 66,
        }
      : {px: 0}),
  };

  const BreadcrumbTemplate = () => (
    <>
      {breadcrumbElements?.length > 0 && (
        <BreadcrumbJsonLd
          itemListElements={getAppendedLinkForBreadcrumb(breadcrumbElements)}
        />
      )}
      {breadcrumbElements?.length > 0 && (
        <Box
          sx={
            isAppIimjobs
              ? {pb: 2}
              : {
                  borderBottom: '1px solid #f2f2f2',
                  borderTop: '1px solid #e6e6e6',
                  py: '9px',
                  pr: '50px',
                  pl: 2,
                  height: '34px',
                  width: '100%',
                  maxWidth: '1440px',
                  zIndex: 3,
                  position: 'fixed',
                  background: '#fff',
                  boxShadow: '0 4px 15px 0 rgba(0,0,0,.06)',
                }
          }
        >
          <Breadcrumbs
            variant={VersionVariants.VERSION_TWO}
            separator={
              <Typography
                fontSize={10}
                color={isAppIimjobs ? 'secondary.main' : '#898989'}
                mx={isAppIimjobs ? 0 : 1.25}
              >
                {'>'}
              </Typography>
            }
            breadcrumbs={breadCrumbs}
          />
        </Box>
      )}
    </>
  );

  return (
    <ErrorBoundary>
      <Box
        display={'flex'}
        justifyContent={'center'}
        sx={{
          ...boxSx,
          ...(page?.indexOf(PERSONALIZE_JOBFEED) > -1 ||
          page?.indexOf(COURSE) > -1
            ? {paddingBottom: '0px !important'}
            : {}),
        }}
      >
        {!isAppIimjobs && <BreadcrumbTemplate />}
        <Box maxWidth={1440} width={'100%'}>
          {isAppIimjobs && <BreadcrumbTemplate />}
          {children}
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default ResponsiveLayout;
